import React, { useState, useEffect, useRef } from "react";
import { RegisterOptions, UseFormMethods } from "react-hook-form";

// Redecalarando forwardRef para funcionar com generics
// Não muda o runtime, somente o typecheck
// Alteração é limitada ao escopo desse módulo
declare module "react" {
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export interface ILazySelectProps<T>
    extends React.DetailedHTMLProps<
            React.SelectHTMLAttributes<HTMLSelectElement>,
            HTMLSelectElement
        >,
        Partial<UseFormMethods> {
    label?: string;
    controller: (...parents: number[]) => Promise<Array<T>>;
    getKeyValue: (option: T) => string | number | undefined;
    getDS: (option: T) => string | undefined;
    fatherName?: string;
    parentsNames?: string[];

    useIndexAsValue?: true | undefined;

    filter?: (options: T) => boolean;
    onOptionsLoad?: (options: Array<T>) => void;

    registerOptions?: RegisterOptions;
}

function LazySelect<T>(
    props: ILazySelectProps<T>,
    ref: React.ForwardedRef<HTMLSelectElement>
) {
    const [lstOptionsNonFiltered, setLstOptionsNonFiltered] = useState<
        Array<T>
    >([]);
    const [lstOptions, setLstOptions] = useState<Array<T>>([]);
    const memory = useRef(new Map<string, Array<T>>());

    const {
        label,
        controller,
        getKeyValue,
        getDS,
        fatherName,
        parentsNames,
        registerOptions,
        onOptionsLoad,
        filter,
        useIndexAsValue,
        register,
        unregister,
        setValue,
        setError,
        getValues,
        watch,
        trigger,
        errors,
        formState,
        reset,
        handleSubmit,
        control,
        clearErrors, //FormMethods
        ...selectProps
    } = props;

    const fatherWatcher = !!(watch && fatherName) && watch(fatherName);
    const parentsWatcher = !!(watch && parentsNames) && watch(parentsNames);

    useEffect(() => {
        let mounted = true;
        /* Convertendo de objeto para array
         * [ "input1", "input2" ]  (parentsNames)
         * {                       (parentsWatcher)
         *     "input1": valor1,
         *     "input2": valor2
         * }
         * [ valor1, valor2 ]      (parentsArray)
         */
        const parentsArray: Array<number> | false =
            !!(parentsWatcher && parentsNames) &&
            parentsNames.map((name) => parentsWatcher[name]);

        /**
         * (!fatherName || fatherWatcher) : não irá executar se houver dependência (fatherName), mas que ainda não foi preenchida (fatherWatcher)
         * (!parentsNames || (parentsArray && parentsArray.every(v=>!!v)) : não irá executar se houver dependência múltipla (parentsNames), mas que todas ainda não foram preenchida (parentsArray.every(v=>!!v)))
         */
        if (
            (!fatherName || fatherWatcher) &&
            (!parentsNames || (parentsArray && parentsArray.every((v) => !!v)))
        ) {
            // gerando key do map de memória
            const mapKey = parentsArray
                ? parentsArray.join("-")
                : `${fatherWatcher}`;

            const exec =
                (fatherName || parentsNames) && memory.current.has(mapKey)
                    ? async () => memory.current.get(mapKey) ?? []
                    : async () =>
                          parentsArray
                              ? controller(...parentsArray)
                              : controller(fatherWatcher || 0);

            exec().then((res) => {
                if (res && mounted) {
                    setLstOptionsNonFiltered(res);
                    memory.current.set(mapKey, res);
                }
            });
        }

        return () => {
            mounted = false;
        };
    }, [fatherWatcher, fatherName, controller, parentsWatcher, parentsNames]);

    useEffect(() => {
        setLstOptions(
            filter
                ? lstOptionsNonFiltered.filter(filter)
                : lstOptionsNonFiltered
        );
    }, [lstOptionsNonFiltered, filter]);

    useEffect(() => {
        if (onOptionsLoad) onOptionsLoad(lstOptions);
    }, [lstOptions, onOptionsLoad]);

    useEffect(() => {
        if (setValue && props.name) setValue(props.name, props.defaultValue);
    }, [lstOptions, props.defaultValue, props.name, setValue]);

    return (
        <>
            {props.label && <label>{props.label}</label>}
            <select
                {...selectProps}
                ref={ref ?? (register ? register(registerOptions) : undefined)}
            >
                {props.children}
                {lstOptions.map((option, index) => (
                    <option
                        value={
                            useIndexAsValue === true
                                ? index
                                : props.getKeyValue(option)
                        }
                        key={props.getKeyValue(option)}
                    >
                        {props.getDS(option)}
                    </option>
                ))}
            </select>
        </>
    );
}

/**
 * Select com Lazy Loading
 * @param controller função que retorna
 * vetor com valores para preencher options -
 * recomendável usar função imutável (controller)
 * ou o useCallback do React
 * @param getKeyValue callback que retorna
 * a string ou number correspondete ao value do option
 * @param getDs callback que retorna
 * a string correspondete ao children do option
 * @param label string opcional, label
 * do componente
 * @param fatherName name do input que esse select
 * é dependente
 * @param parentsNames semelhante ao fatherName mas
 * para dependência múltipla
 * @param onOptionsLoad função executada depois de carregar
 * dados de controller, tem os resultados da mesma como parâmetro
 * @param filter recebe um filtro para os dados de options
 * @params props do retorno do useForm - passar o setValue quando
 * tiver um valor default, e passar o watch quando o select
 * for dependente de outro
 * @params props de um componente select padrão
 * @param registerOptions opções para passar no
 * register() * melhor opção é passar pelo ref normalmente *
 * (register, getValue, etc...)
 * @returns componente select cujo options vêm
 * de requisição
 */
export default React.forwardRef(LazySelect);
// é preciso usar o React.forwardRef aqui pois o componente recebe uma ref
// que será passada para frente, referenciando o select. Componentes de
// função não podem receber refs, por isso essa abordagem é necessária
